import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Check, Layers, Loader2, Search, UserPlus, Users, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
export const ROLE_LABEL = { admin: 'Admin', manager: 'Manager', director: 'Director', broker: 'Broker', broker_support: 'Broker support', credit: 'Credit', settlements: 'Settlements', leads_viewer: 'Leads viewer' };
export const initialsOf = (n = '') => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

export const Avatar = ({ name, size = 'h-8 w-8 text-[11px]', className = '' }) => (
  <span className={`inline-flex shrink-0 items-center justify-center rounded-full bg-[#15133A] font-black text-white ${size} ${className}`}>{initialsOf(name)}</span>
);

/* Shared member picker: search the DerwentAI user directory and tick people. */
export function MemberPicker({ users, selected, onToggle, exclude = new Set(), testPrefix = 'pick' }) {
  const [q, setQ] = useState('');
  const list = useMemo(() => users.filter((u) => !q || `${u.name} ${u.role} ${u.email || ''}`.toLowerCase().includes(q.toLowerCase())), [users, q]);
  return (
    <div>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team members…" autoComplete="off" data-testid={`${testPrefix}-search`}
          className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#15133A]" />
      </div>
      <ul className="max-h-[300px] divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200" data-testid={`${testPrefix}-list`}>
        {list.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400">No team members match.</li>}
        {list.map((u) => {
          const on = selected.has(u.id), dis = exclude.has(u.id);
          return (
            <li key={u.id}>
              <button type="button" disabled={dis} onClick={() => onToggle(u.id)} data-testid={`${testPrefix}-user-${u.id}`} aria-pressed={on}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${dis ? 'opacity-50' : 'hover:bg-slate-50'}`}>
                <Avatar name={u.name} />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-slate-900">{u.name}</span><span className="block truncate text-[11px] text-slate-500">{ROLE_LABEL[u.role] || u.role}{u.email ? ` · ${u.email}` : ''}</span></span>
                {dis ? <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Assigned</span>
                  : <span className={`grid h-5 w-5 place-items-center rounded-md border ${on ? 'border-[#15133A] bg-[#15133A] text-white' : 'border-slate-300 bg-white'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Assign Team: Option A pick individuals · Option B apply a saved template (add or replace). */
export function AssignTeamDialog({ appId, headers, members, onClose, onChanged }) {
  const [mode, setMode] = useState('individual');
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [tid, setTid] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const assigned = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);

  useEffect(() => {
    (async () => {
      try {
        const [d, t] = await Promise.all([axios.get(`${API}/api/onboarding/team/directory`, { headers }), axios.get(`${API}/api/onboarding/team/templates`, { headers })]);
        setUsers(d.data.users || []); setTemplates(t.data.templates || []);
        const def = (t.data.templates || []).find((x) => x.is_default); if (def) setTid(def.id);
      } catch { toast.error('Could not load team directory'); }
    })();
  }, [headers]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tpl = templates.find((t) => t.id === tid);
  const newFromTpl = tpl ? tpl.members.filter((m) => !assigned.has(m.id)) : [];
  const wouldRemove = tpl ? members.filter((m) => !tpl.member_ids.includes(m.user_id)) : [];

  const saveIndividual = async () => {
    if (!sel.size) { toast.error('Select at least one team member'); return; }
    setBusy(true);
    try { const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/team`, { user_ids: [...sel] }, { headers }); toast.success(`${data.added.length} team member${data.added.length === 1 ? '' : 's'} assigned`); onChanged(data.members); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); } finally { setBusy(false); }
  };
  const apply = async (replace) => {
    if (!tid) { toast.error('Choose a template'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/team/apply-template`, { template_id: tid, mode: replace ? 'replace' : 'add' }, { headers });
      toast.success(`${data.template} applied · ${data.added.length} added${data.removed?.length ? `, ${data.removed.length} removed` : ''}`);
      onChanged(data.members); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply template'); } finally { setBusy(false); setConfirmReplace(false); }
  };

  const Tab = ({ k, Icon, label }) => <button type="button" onClick={() => setMode(k)} data-testid={`assign-mode-${k}`} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${mode === k ? 'bg-[#15133A] text-white' : 'text-slate-600 hover:bg-white'}`}><Icon className="h-3.5 w-3.5" />{label}</button>;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 backdrop-blur-sm sm:items-center sm:p-4" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="assign-team-dialog">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div><h2 className="flex items-center gap-2 font-[Outfit] text-base font-black text-slate-900"><Users className="h-4 w-4 text-[#15133A]" /> Assign team</h2><p className="mt-0.5 text-xs text-slate-500">Assigned members can open this file with their own login.</p></div>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid="assign-team-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div className="flex gap-1 rounded-xl bg-slate-100 p-1"><Tab k="individual" Icon={UserPlus} label="Assign individually" /><Tab k="template" Icon={Layers} label="Apply team template" /></div>

          {mode === 'individual' && (
            <>
              <MemberPicker users={users} selected={sel} onToggle={toggle} exclude={assigned} testPrefix="assign" />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500" data-testid="assign-selected-count">{sel.size} selected</span>
                <button type="button" onClick={saveIndividual} disabled={busy || !sel.size} data-testid="assign-save" className="inline-flex items-center gap-1.5 rounded-xl bg-[#15133A] px-4 py-2 text-xs font-bold text-white hover:bg-[#1f1c52] disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save</button>
              </div>
            </>
          )}

          {mode === 'template' && (
            <>
              {templates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-500" data-testid="assign-no-templates">No team templates yet. Create one under <b>Manage Team Templates</b> in the Client Portal.</div>
              ) : (
                <>
                  <label className="block"><span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Select template</span>
                    <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="assign-template-select" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold outline-none focus:border-[#15133A]">
                      <option value="">Choose…</option>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''} · {t.members.length}</option>)}
                    </select>
                  </label>
                  {tpl && (
                    <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200" data-testid="assign-template-members">
                      {tpl.members.map((m) => (
                        <li key={m.id} className="flex items-center gap-3 px-3 py-2.5"><Avatar name={m.name} /><span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-slate-900">{m.name}</span><span className="block text-[11px] text-slate-500">{ROLE_LABEL[m.role] || m.role}</span></span>
                          {assigned.has(m.id) ? <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Already assigned</span> : <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">Will be added</span>}</li>
                      ))}
                      {tpl.members.length === 0 && <li className="px-3 py-4 text-center text-xs text-slate-400">This template has no members.</li>}
                    </ul>
                  )}
                  {tpl && !confirmReplace && (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {members.length > 0 && <button type="button" onClick={() => setConfirmReplace(true)} disabled={busy} data-testid="assign-replace" className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:border-slate-400">Replace current team</button>}
                      <button type="button" onClick={() => apply(false)} disabled={busy || !newFromTpl.length} data-testid="assign-apply" className="inline-flex items-center gap-1.5 rounded-xl bg-[#15133A] px-4 py-2 text-xs font-bold text-white hover:bg-[#1f1c52] disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />} Apply team{newFromTpl.length ? ` · add ${newFromTpl.length}` : ''}</button>
                    </div>
                  )}
                  {tpl && confirmReplace && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="assign-replace-confirm">
                      <p className="font-bold">Replace the current team with {tpl.name}?</p>
                      <p className="mt-1">{wouldRemove.length ? `${wouldRemove.map((m) => m.name).join(', ')} will lose access to this file.` : 'Nobody will be removed.'} {newFromTpl.length} will be added. The saved template is not changed.</p>
                      <div className="mt-3 flex justify-end gap-2">
                        <button type="button" onClick={() => setConfirmReplace(false)} className="rounded-lg px-3 py-1.5 font-bold text-amber-900 hover:bg-amber-100" data-testid="assign-replace-cancel">Cancel</button>
                        <button type="button" onClick={() => apply(true)} disabled={busy} className="rounded-lg bg-amber-600 px-3 py-1.5 font-bold text-white hover:bg-amber-700" data-testid="assign-replace-confirm-btn">Yes, replace team</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
